// -----------------------------------------------------------
// FloraIQ - Temporal3DComparison — Aba Comparação Temporal
// -----------------------------------------------------------

import { useState } from "react";
import { temporalComparisons } from "@/mocks/pointcloud";

export default function Temporal3DComparison() {
  const [sliderPos, setSliderPos] = useState(50);

  return (
    <div className="space-y-5">
      {/* Painel visual antes/depois */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 font-heading mb-3">Comparação Visual — Setor Central</h4>
          <div className="relative h-[260px] rounded-lg overflow-hidden bg-[#0D1117]">
            {/* Fundo simulado */}
            <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "20px 20px" }}></div>

            {/* Label esquerda */}
            <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-md px-2.5 py-1 text-[10px] text-white/80 font-mono">
              2024 — Ortofoto RGB
            </div>

            {/* Label direita */}
            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-2.5 py-1 text-[10px] text-white/80 font-mono">
              2026 — LiDAR + RGB
            </div>

            {/* Pontos "antes" (lado esquerdo) */}
            {Array.from({ length: 30 }).map((_, i) => (
              <div key={`antes-${i}`} className="absolute w-1 h-1 rounded-full bg-white/40"
                style={{ left: `${5 + Math.random() * 45}%`, top: `${30 + Math.random() * 50}%` }}></div>
            ))}

            {/* Pontos "depois" (lado direito) — mais verdes */}
            {Array.from({ length: 40 }).map((_, i) => (
              <div key={`depois-${i}`} className="absolute w-1.5 h-1.5 rounded-full"
                style={{
                  left: `${50 + Math.random() * 45}%`,
                  top: `${25 + Math.random() * 55}%`,
                  backgroundColor: i < 25 ? "#4CAF50" : i < 35 ? "#2E7D32" : "#F57C00",
                  opacity: 0.7,
                }}></div>
            ))}

            {/* Linha divisória com slider */}
            <div className="absolute top-0 bottom-0 w-0.5 bg-white/80" style={{ left: `${sliderPos}%` }}></div>
            <div
              className="absolute top-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer border-2 border-primary-500"
              style={{ left: `calc(${sliderPos}% - 16px)` }}
            >
              <i className="ri-arrow-left-right-line text-primary-500 text-sm"></i>
            </div>
          </div>

          {/* Slider control */}
          <div className="mt-3">
            <input
              type="range"
              min={5}
              max={95}
              value={sliderPos}
              onChange={(e) => setSliderPos(Number(e.target.value))}
              className="w-full accent-primary-500 cursor-pointer"
              aria-label="Comparar antes e depois"
            />
            <div className="flex justify-between text-[10px] text-foreground-400 mt-1">
              <span>Levantamento 2024</span>
              <span>Levantamento 2026</span>
            </div>
          </div>
        </div>

        {/* Gráfico de evolução temporal */}
        <div className="bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 font-heading mb-3">Evolução Temporal — Alturas Extraídas</h4>
          <div className="space-y-3">
            {[
              { label: "Jatobá CTA-2024-09105", anos: [{ a: 2024, v: 17.5 }, { a: 2025, v: 17.9 }, { a: 2026, v: 18.2 }] },
              { label: "Monguba CTA-2024-09104", anos: [{ a: 2024, v: 13.8 }, { a: 2025, v: 14.0 }, { a: 2026, v: 14.2 }] },
              { label: "Sibipiruna CTA-2024-08421", anos: [{ a: 2024, v: 8.2 }, { a: 2025, v: 8.3 }, { a: 2026, v: 8.5 }] },
            ].map((tree) => (
              <div key={tree.label} className="flex items-center gap-3">
                <span className="text-xs text-foreground-600 w-36 whitespace-nowrap truncate">{tree.label}</span>
                <div className="flex-1 flex items-end gap-6 h-8">
                  {tree.anos.map((ano) => (
                    <div key={ano.a} className="flex flex-col items-center gap-0.5">
                      <span className="text-[10px] text-foreground-500">{ano.v}m</span>
                      <div className="w-6 bg-primary-500 rounded-t-sm transition-all duration-500" style={{ height: `${(ano.v / 20) * 32}px`, minHeight: "4px" }}></div>
                      <span className="text-[10px] text-foreground-400">{ano.a}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-background-100/50">
            <p className="text-xs text-foreground-500 flex items-center gap-1.5">
              <i className="ri-information-line"></i>
              Preparado para integração com detecção automática de mudanças via PDAL/CloudCompare
            </p>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-50/50">
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Unidade/Árvore</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Métrica Anterior</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Métrica Atual</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Diferença</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Evento Provável</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Confiança</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
              </tr>
            </thead>
            <tbody>
              {temporalComparisons.map((tc) => (
                <tr key={tc.id} className="border-b border-background-100/50 hover:bg-background-50/30 transition-colors">
                  <td className="px-3 py-2 text-xs font-medium text-foreground-800 whitespace-nowrap font-mono">{tc.unidade}</td>
                  <td className="px-3 py-2 text-xs text-foreground-500 whitespace-nowrap">{tc.metricaAnterior}</td>
                  <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{tc.metricaAtual}</td>
                  <td className={`px-3 py-2 text-xs font-medium whitespace-nowrap ${tc.diferenca.startsWith("+") ? "text-green-600" : tc.diferenca.startsWith("-") ? "text-red-600" : "text-foreground-600"}`}>
                    {tc.diferenca}
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground-700 whitespace-nowrap">{tc.eventoProvavel}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full border whitespace-nowrap ${
                      tc.confianca === "alta" ? "bg-green-50 text-green-700 border-green-200" :
                      tc.confianca === "media" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      "bg-red-50 text-red-700 border-red-200"
                    }`}>
                      {tc.confianca}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Ver detalhes" aria-label="Ver detalhes">
                        <i className="ri-eye-line text-foreground-500 text-sm"></i>
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Gerar relatório" aria-label="Gerar relatório">
                        <i className="ri-file-chart-line text-foreground-500 text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}